/**
 * Feature flags — loaded from the server after login.
 *
 * Two-phase loading:
 *
 *   1. Pre-login: GET /api/config (public) returns opaque f<n> booleans driven
 *      purely by FEATURE_* env vars. The opaque key naming keeps module names
 *      out of DevTools for logged-out visitors.
 *   2. Post-login: GET /api/features (auth-required) returns the merged view
 *      (env-var gate + any future admin DB toggles).
 *
 * Default: all of the modules we ship today are ON until the server responds,
 * to avoid flashing hidden modules on first paint.
 */

import {
  createContext, useContext, useState, useEffect, type ReactNode,
} from "react";

export interface Features {
  photos:             boolean;
  schedule:           boolean;     // gates /calendar
  team:               boolean;     // gates /engineers
  // Fire-safety domain modules
  assets:             boolean;
  defects:            boolean;
  certificates:       boolean;
  riskAssessments:    boolean;
  contracts:          boolean;
  recurringSchedules: boolean;
}

const DEFAULTS: Features = {
  photos:             true,
  schedule:           true,
  team:               true,
  assets:             true,
  defects:            true,
  certificates:       false,
  riskAssessments:    false,
  contracts:          false,
  recurringSchedules: false,
};

/** Map opaque server keys → semantic client keys */
function parseConfig(raw: Record<string, boolean>): Features {
  return {
    photos:             raw["f2"]  ?? true,
    // Calendar reuses the legacy `f5` (schedule) key from the platform skeleton
    schedule:           raw["f5"]  ?? true,
    team:               raw["f6"]  ?? true,
    assets:             raw["f12"] ?? true,
    defects:            raw["f13"] ?? true,
    certificates:       raw["f14"] ?? false,
    riskAssessments:    raw["f15"] ?? false,
    contracts:          raw["f16"] ?? false,
    recurringSchedules: raw["f17"] ?? false,
  };
}

export type FeatureKey = keyof Features;

const FeaturesContext = createContext<Features>(DEFAULTS);

export function FeaturesProvider({ children }: { children: ReactNode }) {
  const [features, setFeatures] = useState<Features>(DEFAULTS);

  useEffect(() => {
    fetch("/api/config")
      .then(r => r.ok ? r.json() : Promise.reject())
      .then((raw: Record<string, boolean>) => setFeatures(parseConfig(raw)))
      .catch(() => { /* keep defaults */ });
  }, []);

  useEffect(() => {
    function loadAuthFeatures() {
      const token = sessionStorage.getItem("fso_tok");
      if (!token) return;
      fetch("/api/features", {
        headers: { Authorization: `Bearer ${token}` },
      })
        .then(r => r.ok ? r.json() : Promise.reject())
        .then((raw: Record<string, boolean>) => setFeatures(parseConfig(raw)))
        .catch(() => { /* keep last known state */ });
    }

    loadAuthFeatures();
    window.addEventListener("fso:login", loadAuthFeatures);
    return () => window.removeEventListener("fso:login", loadAuthFeatures);
  }, []);

  return (
    <FeaturesContext.Provider value={features}>
      {children}
    </FeaturesContext.Provider>
  );
}

export function useFeatures(): Features {
  return useContext(FeaturesContext);
}

/**
 * Convenience singleton for non-hook usage. Always returns the defaults —
 * for runtime gating use useFeatures() inside components.
 */
export const features = DEFAULTS;
